'use client'
import * as React from 'react'
import { ToolProgress } from '@/components/tools/tool-progress'
import { Loader2, Wand2, Sparkles, Copy, CheckCheck, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NewsletterCapture } from '@/components/marketing/newsletter-capture'

type HookStyle = 'problem-solution' | 'social-proof' | 'unboxing' | 'before-after' | 'mistake' | 'listicle'
type AdScript = { angle: string; hook: string; beats: string[]; cta: string }
type AdScriptPack = { hookVariations: string[]; scripts: AdScript[] }
type Response = {
  input: { product: string; benefit: string; audience: string; hookStyle: HookStyle; detail?: string }
  templatePack: AdScriptPack
  aiScript: string | null
  aiEnabled: boolean
}

const HOOK_STYLES: { value: HookStyle; label: string }[] = [
  { value: 'problem-solution', label: 'Problem → solution' },
  { value: 'social-proof', label: 'Social proof' },
  { value: 'unboxing', label: 'Unboxing / first impression' },
  { value: 'before-after', label: 'Before → after' },
  { value: 'mistake', label: 'Mistake / myth-bust' },
  { value: 'listicle', label: 'Listicle ("3 reasons…")' },
]

function packToText(p: AdScriptPack): string {
  const lines: string[] = ['HOOK VARIATIONS', ...p.hookVariations.map((h) => `• ${h}`), '']
  p.scripts.forEach((s, i) => {
    lines.push(`SCRIPT ${i + 1} — ${s.angle}`, `Hook: ${s.hook}`, ...s.beats, `CTA: ${s.cta}`, '')
  })
  return lines.join('\n')
}

export function AdScriptGenerator() {
  const [product, setProduct] = React.useState('')
  const [benefit, setBenefit] = React.useState('')
  const [audience, setAudience] = React.useState('')
  const [detail, setDetail] = React.useState('')
  const [hookStyle, setHookStyle] = React.useState<HookStyle>('problem-solution')
  const [loading, setLoading] = React.useState(false)
  const [data, setData] = React.useState<Response | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [copied, setCopied] = React.useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!product.trim() || !benefit.trim() || !audience.trim()) return
    setLoading(true)
    setError(null)
    setData(null)
    setCopied(false)
    try {
      const res = await fetch('/api/tools/ad-script-generator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product: product.trim(), benefit: benefit.trim(), audience: audience.trim(), detail: detail.trim(), hookStyle }),
      })
      const json = await res.json()
      if (!res.ok) setError(json?.error || 'Generation failed. Check your inputs.')
      else setData(json as Response)
    } catch {
      setError('Network error. Try again.')
    } finally {
      setLoading(false)
    }
  }

  function onCopy() {
    if (!data) return
    navigator.clipboard?.writeText(packToText(data.templatePack)).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000) },
      () => {},
    )
  }

  function fillSample() {
    setProduct('Airlift High-Waist Suit Up Shorts')
    setBenefit('No ride-up through a full workout')
    setAudience('women who lift and run')
    setDetail('the high waistband staying put during squats')
    setHookStyle('before-after')
    setData(null)
  }

  return (
    <div className="w-full max-w-3xl">
      <form onSubmit={onSubmit} className="rounded-2xl border bg-card p-5 sm:p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Product name" required>
            <Input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Airlift High-Waist Shorts" />
          </Field>
          <Field label="Audience" required>
            <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="women who lift and run" />
          </Field>
          <Field label="Key benefit" required className="sm:col-span-2">
            <Input value={benefit} onChange={(e) => setBenefit(e.target.value)} placeholder="No ride-up through a full workout" />
          </Field>
          <Field label="A detail to show on camera (optional)" className="sm:col-span-2">
            <Input value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="the high waistband staying put during squats" />
          </Field>
          <Field label="Hook style" className="sm:col-span-2">
            <div className="flex flex-wrap gap-2">
              {HOOK_STYLES.map((h) => (
                <button
                  key={h.value}
                  type="button"
                  onClick={() => setHookStyle(h.value)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${hookStyle === h.value ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
                >
                  {h.label}
                </button>
              ))}
            </div>
          </Field>
        </div>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button type="button" onClick={fillSample} className="text-left text-xs font-medium text-primary underline underline-offset-4 hover:text-primary/80">
            Fill with a sample
          </button>
          <Button type="submit" size="lg" disabled={loading || !product.trim() || !benefit.trim() || !audience.trim()} className="shrink-0">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Writing</> : <>Generate scripts <Wand2 className="ml-2 h-4 w-4" /></>}
          </Button>
        </div>
      </form>

      {loading && (
        <ToolProgress label="your brief" steps={[{ title: "Reading your brief", sub: "Product, benefit, audience, hook style" }, { title: "Drafting hooks", sub: "Native, scroll-stopping openers" }, { title: "Writing the scripts", sub: "Timecoded beats with CTAs" }]} />
      )}

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {data && (
        <div className="mt-8 space-y-6">
          {data.aiEnabled && data.aiScript && (
            <div className="rounded-2xl border-2 border-primary/25 bg-gradient-to-br from-primary/[0.06] to-card p-5 sm:p-6">
              <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.22em] text-primary">
                <Sparkles className="h-3.5 w-3.5" /> AI-written scripts
              </p>
              <p className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/90">{data.aiScript}</p>
            </div>
          )}

          {/* Deterministic template pack */}
          <div className="rounded-2xl border bg-card p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                {data.aiEnabled && data.aiScript ? 'Template pack (always-on)' : 'Your scripts'}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={onCopy} className="shrink-0">
                {copied ? <><CheckCheck className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy all</>}
              </Button>
            </div>

            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Hook variations</p>
              <ul className="mt-2 space-y-1.5">
                {data.templatePack.hookVariations.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-[15px] leading-relaxed text-foreground/90">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-5 grid gap-4">
              {data.templatePack.scripts.map((s, i) => (
                <div key={i} className="rounded-xl border bg-background p-4">
                  <p className="font-display text-sm font-semibold text-foreground">Script {i + 1}: {s.angle}</p>
                  <p className="mt-1.5 text-sm font-medium text-foreground/90">Hook: {s.hook}</p>
                  <ul className="mt-2 space-y-1 text-sm text-foreground/80">
                    {s.beats.map((b, j) => <li key={j}>{b}</li>)}
                  </ul>
                  <p className="mt-2 text-sm font-medium text-foreground">CTA: {s.cta}</p>
                </div>
              ))}
            </div>
          </div>

          {!data.aiEnabled && (
            <div className="rounded-xl border border-dashed bg-secondary/40 p-4 text-xs text-muted-foreground">
              The AI script writer is off on this deployment, so you are seeing the deterministic template pack, proven hook formulas filled from your inputs. Set ANTHROPIC_API_KEY to enable fresh AI-written scripts. Bracketed lines are prompts for you (or your creator) to fill, the generator never invents claims for you.
            </div>
          )}

          <div className="rounded-2xl border bg-card p-5 sm:p-6">
            <NewsletterCapture
              source="tool:ad-script-generator"
              headline="Get the UGC hook formula library + new teardowns"
              caption="One email with the full hook-formula library this generator runs on, plus new long-form teardowns when they ship. No drip sequence."
              cta="Send it over"
            />
            <p className="mt-4 text-center text-xs text-muted-foreground">
              Idukki turns the customer videos you already collect into shoppable, tagged ad creative.{' '}
              <a href="https://app.idukki.io/register" className="font-medium text-primary underline underline-offset-4">Start a free trial</a>.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, required, className, children }: { label: string; required?: boolean; className?: string; children: React.ReactNode }) {
  return (
    <label className={className}>
      <span className="block text-xs font-medium text-foreground">
        {label}{required && <span className="text-primary"> *</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  )
}
